import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, BarChart3, Layers, Code2, FlaskConical, Repeat } from "lucide-react";
import {
  Nav,
  Footer,
  ClosingV5,
  Eyebrow,
  PillarAccent,
  TestimonialsV5,
} from "../App";
import { useContactPanel } from "../components/ContactPanel";
import { useContent } from "../lib/useContent";
import { useSeo } from "../lib/useSeo";

/* ============================================================
   /conversion-optimization — Sunday Digital
   Built on V5's design system. Signature moment unique to this
   page: an animated "lift funnel" — five stacked bars that draw
   in from the left, representing the 5-step CRO process and the
   compounding lift each step adds. Mirrors the Marketing page's
   FullCircleRing rhythm without re-using it.
============================================================ */

const PrimaryCTA = ({ children, onClick, testId }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-neutral-950 bg-[#F4D64E] hover:bg-white px-8 py-4 rounded-md transition-all duration-300 shadow-[0_12px_40px_-12px_rgba(244,214,78,0.5)] hover:shadow-[0_16px_50px_-12px_rgba(244,214,78,0.65)] hover:-translate-y-0.5"
  >
    {children}
    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
  </button>
);

/* ============================================================
   Signature moment — animated "lift funnel" SVG.
   Five stacked bars, each labeled with a stage; each wider than
   the last to evoke a compounding-lift narrative.
============================================================ */
const LiftFunnel = () => {
  const stages = [
    { label: "Analyze",   pct: 38, kpi: "Baseline" },
    { label: "Structure", pct: 56, kpi: "+18 pts" },
    { label: "Build",     pct: 72, kpi: "+34 pts" },
    { label: "Test",      pct: 86, kpi: "+48 pts" },
    { label: "Iterate",   pct: 96, kpi: "Compound" },
  ];
  // Trigger entrance animation when the funnel enters the viewport,
  // using a local IntersectionObserver. `whileInView` was firing
  // inconsistently on mobile (Safari + Chrome) — leaving bars stuck
  // at width 0 — so we drive the state ourselves and animate on mount.
  const ref = useRef(null);
  const [animateOn, setAnimateOn] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (typeof IntersectionObserver === "undefined") {
      setAnimateOn(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setAnimateOn(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.15 }
    );
    io.observe(el);
    // Fallback: if for any reason the observer never fires within 1.2s
    // of mount (e.g. element already in view + no scroll event), force on.
    const t = setTimeout(() => setAnimateOn(true), 1200);
    return () => {
      io.disconnect();
      clearTimeout(t);
    };
  }, []);
  return (
    <div ref={ref} className="relative w-full">
      {/* Soft brand glow */}
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(600px 400px at 70% 50%, rgba(244,214,78,0.10), transparent 60%)",
        }}
      />
      <div className="relative flex flex-col gap-4">
        {stages.map((s, i) => (
          <motion.div
            key={s.label}
            initial={{ opacity: 0, x: -24 }}
            animate={animateOn ? { opacity: 1, x: 0 } : { opacity: 0, x: -24 }}
            transition={{
              duration: 0.7,
              delay: 0.15 + i * 0.12,
              ease: [0.22, 1, 0.36, 1],
            }}
            className="relative"
          >
            <div className="flex items-center justify-between mb-1.5 text-[0.7rem] font-bold tracking-[0.22em] uppercase">
              <span className="text-neutral-300">
                <span className="text-neutral-500 tabular-nums mr-3">
                  0{i + 1}
                </span>
                {s.label}
              </span>
              <span className="text-[#F4D64E]/85 tabular-nums">{s.kpi}</span>
            </div>
            <div className="relative h-3 rounded-full bg-white/[0.05] overflow-hidden border border-white/[0.06]">
              <motion.span
                initial={{ width: 0 }}
                animate={animateOn ? { width: `${s.pct}%` } : { width: 0 }}
                transition={{
                  duration: 1.1,
                  delay: 0.3 + i * 0.12,
                  ease: [0.22, 1, 0.36, 1],
                }}
                className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#F4D64E] to-[#FFE583] rounded-full"
              />
            </div>
          </motion.div>
        ))}
        {/* Footer caption */}
        <div className="mt-4 pt-4 border-t border-white/[0.06] flex items-center justify-between text-[0.65rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
          <span>Compounding lift</span>
          <span className="tabular-nums">Cycle · ongoing</span>
        </div>
      </div>
    </div>
  );
};

/* ============================================================
   01 · Hero
============================================================ */
const ConversionHero = ({ onPrimary }) => {
  const cms = useContent("conversion");
  const h = (cms && cms.hero) || {};
  const breadcrumb = h.breadcrumb || "Conversion Optimization";
  const eyebrow = h.eyebrow || "CRO · Built in, not bolted on";
  const titleLine1 = h.title_line_1 || "Lift,";
  const titleLine2 = h.title_line_2 || "earned with";
  const titleEmphasis = h.title_emphasis || "process";
  const titleAccent = h.title_accent || "not guesses";
  const subtitle = h.subtitle ||
    "Conversion optimization is a discipline we weave into every Shopify build and growth engagement — analysis, structure, build, test, iterate. The work shows up where it matters: in measurable lift across the funnel.";
  const ctaLabel = h.cta_primary_label || "Start a project";
  const secondaryLabel = h.cta_secondary_label || "See our work";
  const secondaryHref = h.cta_secondary_href || "/showcase";

  return (
    <section
      id="top"
      data-testid="conversion-hero"
      className="relative overflow-hidden bg-[#0B0F14]"
    >
      <div
        aria-hidden
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(900px 600px at 80% 30%, rgba(244,214,78,0.10), transparent 60%)",
        }}
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20 pb-20 md:pb-24">
        <div
          data-reveal="eyebrow"
          className="flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mb-10 md:mb-14"
        >
          <a href="/" className="hover:text-[#F4D64E] transition-colors">Sunday</a>
          <span className="w-1 h-1 rounded-full bg-neutral-700" />
          <span className="text-[#F4D64E]">{breadcrumb}</span>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-16 items-center">
          {/* LEFT — copy */}
          <div className="lg:col-span-7">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>
            <h1
              data-testid="conversion-hero-title"
              className="font-display mt-7 text-[2.5rem] sm:text-[3.5rem] md:text-[4.25rem] lg:text-[4.75rem] font-black tracking-[-0.035em] leading-[0.98] text-white"
            >
              <span data-reveal="mask-block">
                <span>{titleLine1}</span>
              </span>
              <span data-reveal="mask-block" style={{ "--i": 1 }} className="block">
                <span>
                  {titleLine2}{" "}
                  {titleEmphasis ? (
                    <span className="italic font-normal text-neutral-400">{titleEmphasis}</span>
                  ) : null}
                  {titleAccent ? (
                    <>
                      {" "}
                      <PillarAccent>{titleAccent}</PillarAccent>
                    </>
                  ) : null}
                  <span className="text-white">.</span>
                </span>
              </span>
            </h1>
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="mt-9 max-w-[58ch] text-[1.02rem] md:text-[1.15rem] text-neutral-300 leading-[1.6]"
            >
              <span>{subtitle}</span>
            </p>
            <div
              data-reveal="eyebrow"
              style={{ "--i": 3 }}
              className="mt-10 flex flex-wrap items-center gap-4"
            >
              <PrimaryCTA testId="conv-hero-cta" onClick={onPrimary}>
                {ctaLabel}
              </PrimaryCTA>
              <a
                href={secondaryHref}
                className="group inline-flex items-center gap-3 text-[0.85rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] border border-[#F4D64E]/45 hover:bg-[#F4D64E]/10 hover:border-[#F4D64E] px-6 py-4 rounded-md transition-colors"
              >
                {secondaryLabel}
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5" />
              </a>
            </div>
          </div>

          {/* RIGHT — signature lift funnel */}
          <div className="lg:col-span-5 lg:pl-6">
            <LiftFunnel />
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   02 · Editorial — "Why CRO matters" (integration tone)
============================================================ */
const WhyCRO = () => {
  const cms = useContent("conversion");
  const w = (cms && cms.why) || {};
  const eyebrow = w.eyebrow || "Why we care about CRO";
  const mainLead = w.main_lead || "Traffic without conversion is a tax,";
  const italic = w.main_italic || "not an asset.";
  const body = w.body ||
    "Every Sunday engagement, dev or marketing, is shipped with conversion in mind from day one. That means we don't just hand off a beautiful storefront and hope; we instrument it, watch how it performs, and feed what we learn back into the build. CRO sits inside our process — not as a separate retainer, as a discipline.";
  return (
    <section data-testid="conversion-why" className="relative bg-[#0B0F14] border-t border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-10">
          <div className="lg:col-span-4">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>
          </div>
          <div className="lg:col-span-8">
            <h2
              data-reveal="mask-block"
              className="font-display text-[2rem] md:text-[2.75rem] lg:text-[3.25rem] font-black tracking-[-0.03em] leading-[1.04] text-white max-w-[28ch]"
            >
              <span>
                {mainLead}{" "}
                <span className="italic font-normal text-neutral-400">{italic}</span>
              </span>
            </h2>
            <p
              data-reveal="mask-block"
              style={{ "--i": 1 }}
              className="mt-8 max-w-[60ch] text-[1.02rem] md:text-[1.1rem] text-neutral-400 leading-[1.7]"
            >
              <span>{body}</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   03 · Approach — five-step chapter list
============================================================ */
const APPROACH_ICONS = [BarChart3, Layers, Code2, FlaskConical, Repeat];

const APPROACH_DEFAULTS = [
  {
    n: "01",
    title: "Analyze",
    body: "We start with what the data already says — sessions, behavior, friction. Heatmaps, recordings, funnels, Shopify analytics, GA4. Where are visitors stalling, dropping, looping? What's the gap between intent and action?",
    tag: "Traffic · Behavior · Friction",
  },
  {
    n: "02",
    title: "Structure",
    body: "Then we restructure. PDP logic, navigation, bundle and AOV mechanics, cart flow, checkout nudges. Pages stop being collections of components — they become decisions made on purpose, in a deliberate order.",
    tag: "UX flows · PDP · Bundles · AOV",
  },
  {
    n: "03",
    title: "Build",
    body: "Inside Shopify we build to the structure: sections, templates, custom features, landing pages, app integrations. Clean theme code, fast LCP, accessibility checked. The storefront is built once and edited fast.",
    tag: "Shopify · Sections · Templates",
  },
  {
    n: "04",
    title: "Test",
    body: "We run a continuous A/B and multivariate roadmap — hypothesis, prediction, sample size, decision. Wins ship, losses inform the next test. Every experiment is logged so the brand owns a growing playbook of what works.",
    tag: "A/B · MVT · Hypotheses",
  },
  {
    n: "05",
    title: "Iterate",
    body: "Optimization isn't a project, it's a cycle. We revisit the data quarterly, prune what's underperforming, double down on what's lifting, and feed those signals back into marketing, content, and the next build phase.",
    tag: "Compound · Quarterly · Ongoing",
  },
];

const Approach = () => {
  const cms = useContent("conversion");
  const ap = (cms && cms.approach) || {};
  const eyebrow = ap.eyebrow || "Our approach";
  const headlineMain = ap.headline_main || "Five steps,";
  const headlineItalic = ap.headline_italic || "one cycle.";
  const description = ap.description ||
    "Analyze, structure, build, test, iterate — repeated as a cycle, not a project. Every step feeds the next, and what we learn at the end loops back into the start.";
  const items = Array.isArray(ap.items) && ap.items.length
    ? ap.items.map((it, i) => ({
        n: String(i + 1).padStart(2, "0"),
        title: it.title || "",
        body: it.body || "",
        tag: it.tag || "",
      }))
    : APPROACH_DEFAULTS;

  const sectionRef = useRef(null);
  const [active, setActive] = useState(0);

  // Vertical line on the LEFT — fills from 0 → 1 as user scrolls
  // through the section. Same animation pattern as ShopifyDev's
  // "How we work" sticky timeline.
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });
  const lineScaleY = useTransform(scrollYProgress, [0, 1], [0, 1]);

  // Track which right-column card is currently most-visible so the
  // left chip + name highlight in sync. IntersectionObserver feeds
  // setActive as each card enters the viewport center.
  const cardRefs = useRef([]);
  useEffect(() => {
    const els = cardRefs.current.filter(Boolean);
    if (!els.length || typeof IntersectionObserver === "undefined") return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.dataset.idx);
            if (!Number.isNaN(idx)) setActive(idx);
          }
        });
      },
      { threshold: 0.55, rootMargin: "0px 0px -25% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [items.length]);

  return (
    <section
      id="approach"
      data-testid="conversion-approach"
      ref={sectionRef}
      className="relative bg-[#11161D] border-t border-white/[0.06]"
    >
      {/* ============ MOBILE — stacked editorial cards ============ */}
      <div className="lg:hidden">
        <div className="max-w-7xl mx-auto px-6 py-20 md:py-28">
          <div className="mb-12 md:mb-16 flex flex-col gap-5">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>
            <h2
              data-reveal="mask-block"
              style={{ "--i": 1 }}
              className="font-display text-[1.85rem] sm:text-[2.25rem] font-extrabold tracking-[-0.035em] leading-[1.05] text-white"
            >
              <span>
                {headlineMain}{" "}
                <span className="italic font-normal text-neutral-400">{headlineItalic}</span>
              </span>
            </h2>
            <p
              data-reveal="mask-block"
              style={{ "--i": 2 }}
              className="text-[0.98rem] text-neutral-400 leading-[1.65] max-w-[60ch]"
            >
              <span>{description}</span>
            </p>
          </div>
          <ul className="flex flex-col gap-16 md:gap-20">
            {items.map((step, i) => {
              const Icon = APPROACH_ICONS[i % APPROACH_ICONS.length];
              return (
                <li
                  key={step.n + step.title}
                  data-reveal="eyebrow"
                  style={{ "--i": i % 3 }}
                  className="flex flex-col"
                >
                  {/* Number circle */}
                  <span className="inline-grid place-items-center rounded-full bg-[#F4D64E] text-neutral-950 font-display font-black text-[1rem] tabular-nums w-[3.25rem] h-[3.25rem] leading-none pt-[2px]">
                    {step.n}
                  </span>

                  {/* Icon + tag caption */}
                  <div className="mt-8 flex items-center gap-3">
                    <Icon className="w-5 h-5 text-[#F4D64E]" strokeWidth={1.75} />
                    <span className="text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                      {step.tag}
                    </span>
                  </div>

                  {/* Heading */}
                  <h3 className="mt-6 font-display text-[1.85rem] sm:text-[2.1rem] font-black tracking-[-0.025em] leading-[1.05] text-white">
                    {step.title}.
                  </h3>

                  {/* Body */}
                  <p className="mt-5 text-[0.98rem] text-neutral-400 leading-[1.7]">
                    {step.body}
                  </p>
                </li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* ============ DESKTOP — sticky left rail, scrollable right ============ */}
      <div className="hidden lg:block">
        <div className="max-w-7xl mx-auto px-10 py-24 xl:py-28">
          {/* Section header — outside the sticky grid so it scrolls
              away normally before the user reaches the steps. */}
          <div className="grid grid-cols-12 gap-x-12 mb-16">
            <div className="col-span-5">
              <span data-reveal="eyebrow">
                <Eyebrow>{eyebrow}</Eyebrow>
              </span>
              <h2
                data-reveal="mask-block"
                style={{ "--i": 1 }}
                className="mt-6 font-display text-[2.5rem] xl:text-[2.85rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white"
              >
                <span>
                  {headlineMain}{" "}
                  <span className="italic font-normal text-neutral-400">{headlineItalic}</span>
                </span>
              </h2>
            </div>
            <div className="col-span-6 col-start-7 self-end">
              <p
                data-reveal="mask-block"
                style={{ "--i": 2 }}
                className="text-[1.075rem] text-neutral-400 leading-[1.6]"
              >
                <span>{description}</span>
              </p>
            </div>
          </div>

          {/* Two-column area: sticky LEFT rail + scrollable RIGHT cards.
              Same left-side behavior as ShopifyDev's Process — chip +
              active state + yellow drawing line. The right side now
              renders ALL steps stacked, scrolling normally.
              IMPORTANT: no `items-start` on the grid — we WANT the left
              column to stretch to match the right column's height so
              `position: sticky` has room to actually stick. */}
          <div className="grid grid-cols-12 gap-x-12">
            {/* LEFT — sticky vertical timeline */}
            <div className="col-span-5">
              <div className="sticky top-28 relative">
                <span
                  aria-hidden
                  className="absolute left-[19px] top-2 bottom-2 w-px bg-white/[0.08]"
                />
                <motion.span
                  aria-hidden
                  style={{ scaleY: lineScaleY }}
                  className="absolute left-[19px] top-2 bottom-2 w-px bg-[#F4D64E] origin-top"
                />
                <ul className="flex flex-col gap-1">
                  {items.map((step, i) => {
                    const isActive = i === active;
                    const isPassed = i < active;
                    return (
                      <li
                        key={step.n + step.title}
                        data-testid={`approach-rail-${step.n}`}
                        className="relative flex items-center gap-6 py-3"
                      >
                        <span
                          aria-hidden
                          className={`relative z-10 inline-flex items-center justify-center w-10 h-10 rounded-full border-2 text-[0.7rem] font-bold uppercase tabular-nums leading-none transition-all duration-500 ease-[cubic-bezier(0.22,1,0.36,1)] ${
                            isActive
                              ? "bg-[#F4D64E] text-neutral-950 border-[#F4D64E] scale-110"
                              : isPassed
                                ? "bg-[#0B0F14] text-[#F4D64E] border-[#F4D64E]"
                                : "bg-[#0B0F14] text-neutral-500 border-white/15"
                          }`}
                          style={{ letterSpacing: "0" }}
                        >
                          {step.n}
                        </span>
                        <span
                          className={`font-display text-[1.2rem] font-bold tracking-[-0.01em] transition-colors duration-500 ${
                            isActive
                              ? "text-white"
                              : isPassed
                                ? "text-neutral-300"
                                : "text-neutral-500"
                          }`}
                        >
                          {step.title}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>

            {/* RIGHT — scrollable column with all steps stacked */}
            <div className="col-span-7 col-start-7 flex flex-col gap-24 xl:gap-28">
              {items.map((step, i) => {
                const Icon = APPROACH_ICONS[i % APPROACH_ICONS.length];
                return (
                  <article
                    key={step.n + step.title}
                    ref={(el) => (cardRefs.current[i] = el)}
                    data-idx={i}
                    data-testid={`approach-card-${step.n}`}
                    className="relative"
                  >
                    {/* Giant translucent number — visual anchor */}
                    <span
                      aria-hidden
                      className="absolute -top-10 -left-6 font-display font-black text-white/[0.04] leading-none select-none pointer-events-none tabular-nums"
                      style={{ fontSize: "clamp(8rem, 12vw, 12rem)", letterSpacing: "-0.06em" }}
                    >
                      {step.n}
                    </span>
                    <div className="relative">
                      <div className="flex items-center gap-3 mb-5">
                        <span className="flex items-center justify-center w-11 h-11 rounded-full border border-[#F4D64E]/30 bg-[#F4D64E]/[0.06] text-[#F4D64E]">
                          <Icon className="w-5 h-5" strokeWidth={1.75} />
                        </span>
                        <span className="text-[0.7rem] font-bold tracking-[0.3em] uppercase text-[#F4D64E] tabular-nums">
                          Step {step.n}
                        </span>
                      </div>
                      <h3 className="font-display text-[2.5rem] xl:text-[3rem] font-black tracking-[-0.035em] leading-[1.02] text-white">
                        {step.title}.
                      </h3>
                      {step.tag ? (
                        <div className="mt-4 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                          {step.tag}
                        </div>
                      ) : null}
                      <p className="mt-8 max-w-[52ch] text-[1.1rem] xl:text-[1.15rem] text-neutral-300 leading-[1.7]">
                        {step.body}
                      </p>
                      <div className="mt-10 flex items-center gap-3 text-[0.7rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                        <span className="tabular-nums text-[#F4D64E]">
                          {step.n}
                        </span>
                        <span className="h-px w-12 bg-white/[0.1]" />
                        <span className="tabular-nums">
                          of {String(items.length).padStart(2, "0")}
                        </span>
                      </div>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   04 · Core deliverables — 6-tile grid
============================================================ */
const DELIVERABLES_DEFAULTS = [
  { n: "01", title: "Conversion audit", body: "End-to-end review of the funnel: traffic sources, on-site behavior, drop-offs, technical health, and the gap between current and target conversion." },
  { n: "02", title: "Bundle & AOV strategy", body: "Where bundles, cross-sell tiles, and order-bumps fit on PDP, cart, and checkout — and how each is priced to lift average order value without eroding margin." },
  { n: "03", title: "PDP optimization framework", body: "A reusable PDP system: hero, social proof, scarcity, FAQ, sticky atc, swatches, reviews — sequenced for buyer psychology, not for layout symmetry." },
  { n: "04", title: "CRO testing roadmap", body: "A prioritized 6-month plan of hypotheses, sized tests, and expected lift — built from your data, not a generic checklist." },
  { n: "05", title: "Technical build direction", body: "Decisions about theme architecture, app sprawl, page builders vs. custom sections, Shopify Plus features — all scoped to keep the storefront fast and editable." },
  { n: "06", title: "Post-launch optimization plan", body: "What changes once the storefront is live: a recurring rhythm of analytics review, experiment shipping, and content refresh tied to measurable goals." },
];

/* ============================================================
   Right-panel visuals — one per deliverable. Lightweight SVG-ish
   mocks, all wrapped in a shared 4/3 aspect frame so the panel
   never jumps height between swaps. No timers inside each visual
   — entrance animation only.
============================================================ */
const VisualFrame = ({ tag, children }) => (
  <div className="relative w-full aspect-[1/1] sm:aspect-[4/3] bg-[#0B0F14] border border-white/[0.08] rounded-2xl overflow-hidden">
    <span className="absolute top-0 left-0 right-0 px-5 py-3 text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 z-10 bg-gradient-to-b from-[#0B0F14] via-[#0B0F14]/95 to-transparent">
      {tag}
    </span>
    <div className="absolute inset-0 flex items-center justify-center p-5 pt-14 sm:p-8 sm:pt-14">
      {children}
    </div>
  </div>
);

// 01 · Conversion audit → 4-stage funnel
const FunnelVisual = () => {
  const rows = [
    { label: "Sessions",   pct: 100, val: "100%" },
    { label: "PDP views",  pct: 64,  val: "64%" },
    { label: "Add to cart", pct: 32, val: "32%" },
    { label: "Checkout",   pct: 11,  val: "11%" },
  ];
  return (
    <VisualFrame tag="Funnel · Diagnostic">
      <div className="w-full max-w-md flex flex-col gap-3">
        {rows.map((r, i) => (
          <motion.div
            key={r.label}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.55, delay: 0.1 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center gap-4"
          >
            <span className="w-24 text-[0.72rem] font-bold tracking-[0.18em] uppercase text-neutral-400 shrink-0">
              {r.label}
            </span>
            <div className="relative h-3 flex-1 rounded-full bg-white/[0.05] overflow-hidden">
              <motion.span
                initial={{ width: 0 }}
                animate={{ width: `${r.pct}%` }}
                transition={{ duration: 0.9, delay: 0.2 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-y-0 left-0 rounded-full bg-gradient-to-r from-[#F4D64E] to-[#FFE583]"
              />
            </div>
            <span className="w-12 text-[0.78rem] font-bold tabular-nums text-white text-right">
              {r.val}
            </span>
          </motion.div>
        ))}
      </div>
    </VisualFrame>
  );
};

// 02 · Bundle & AOV → stylized cart with bundle line + animated total
const AOVVisual = () => {
  const lines = [
    { label: "Hydrating serum", price: "$52" },
    { label: "Travel pouch",    price: "$22" },
  ];
  return (
    <VisualFrame tag="Cart · Bundle uplift">
      <div className="w-full max-w-md flex flex-col gap-2.5">
        {lines.map((l, i) => (
          <motion.div
            key={l.label}
            initial={{ opacity: 0, x: -16 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.15 + i * 0.1, ease: [0.22, 1, 0.36, 1] }}
            className="flex items-center justify-between px-4 py-3 rounded-md bg-white/[0.04] border border-white/[0.05]"
          >
            <span className="text-[0.85rem] text-neutral-300">{l.label}</span>
            <span className="text-[0.85rem] font-bold tabular-nums text-white">{l.price}</span>
          </motion.div>
        ))}
        {/* Bundle suggestion */}
        <motion.div
          initial={{ opacity: 0, x: -16, scale: 0.96 }}
          animate={{ opacity: 1, x: 0, scale: 1 }}
          transition={{ duration: 0.55, delay: 0.55, ease: [0.22, 1, 0.36, 1] }}
          className="flex items-center justify-between px-4 py-3 rounded-md bg-[#F4D64E]/[0.10] border border-[#F4D64E]/40"
        >
          <span className="flex items-center gap-2">
            <span className="text-[0.55rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E] px-2 py-0.5 rounded-full border border-[#F4D64E]/50">
              Bundle
            </span>
            <span className="text-[0.85rem] text-white">+ Sleep mask</span>
          </span>
          <span className="text-[0.85rem] font-bold tabular-nums text-[#F4D64E]">+$38</span>
        </motion.div>
        {/* Total row */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.95, ease: [0.22, 1, 0.36, 1] }}
          className="mt-3 pt-4 border-t border-white/[0.08] flex items-end justify-between"
        >
          <div className="flex flex-col">
            <span className="text-[0.6rem] font-bold tracking-[0.22em] uppercase text-neutral-500">Total · After</span>
            <span className="flex items-baseline gap-3 mt-1">
              <span className="text-[0.95rem] text-neutral-500 line-through tabular-nums">$74</span>
              <span className="font-display font-black text-white text-[1.8rem] leading-none tabular-nums">$112</span>
            </span>
          </div>
          <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[#F4D64E] text-neutral-950 text-[0.65rem] font-bold tracking-[0.14em] uppercase tabular-nums">
            +51% AOV
          </span>
        </motion.div>
      </div>
    </VisualFrame>
  );
};

// 03 · PDP optimization → sequenced section build with conversion labels
const PDPVisual = () => {
  // 5 PDP sections appear top-to-bottom. Each has a brand-yellow label
  // that pops in alongside it, signaling the conversion intent of that
  // section. Reads as "this is the framework, in order".
  const sections = [
    { h: "h-9",  w: "w-3/4", label: "Hero · Above the fold" },
    { h: "h-4",  w: "w-1/2", label: "Social proof" },
    { h: "h-14", w: "w-full", label: "Bundle slot" },
    { h: "h-4",  w: "w-2/3", label: "Trust badges" },
    { h: "h-11", w: "w-full", label: "Sticky ATC", accent: true },
  ];
  return (
    <VisualFrame tag="PDP · Framework">
      <div className="w-full max-w-md grid grid-cols-[6.5rem_1fr] sm:grid-cols-[5.5rem_1fr] gap-x-4 sm:gap-x-5 gap-y-3 items-center">
        {sections.flatMap((s, i) => [
          <motion.span
            key={`l-${i}`}
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.4, delay: 0.2 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
            className="text-right text-[0.6rem] font-bold tracking-[0.2em] uppercase text-[#F4D64E] leading-tight"
          >
            {s.label}
          </motion.span>,
          <motion.div
            key={`b-${i}`}
            initial={{ opacity: 0, scaleX: 0.6 }}
            animate={{ opacity: 1, scaleX: 1 }}
            transition={{ duration: 0.5, delay: 0.2 + i * 0.15, ease: [0.22, 1, 0.36, 1] }}
            style={{ transformOrigin: "left" }}
            className={`${s.h} ${s.w} rounded-md ${
              s.accent
                ? "bg-gradient-to-r from-[#F4D64E] to-[#FFE583]"
                : "bg-white/[0.07] border border-white/[0.08]"
            }`}
          />,
        ])}
      </div>
    </VisualFrame>
  );
};

// 04 · CRO testing roadmap → dual-screen comparison with WINNER stamp
const ABTestVisual = () => {
  const Screen = ({ label, dim, conv, delay }) => (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay, ease: [0.22, 1, 0.36, 1] }}
      className={`relative flex flex-col gap-2 p-3 rounded-lg border ${
        dim
          ? "bg-white/[0.03] border-white/[0.06]"
          : "bg-[#F4D64E]/[0.07] border-[#F4D64E]/40"
      }`}
    >
      <div className="flex items-center gap-1.5">
        <span className="w-1.5 h-1.5 rounded-full bg-white/20" />
        <span className="w-1.5 h-1.5 rounded-full bg-white/20" />
        <span className="w-1.5 h-1.5 rounded-full bg-white/20" />
      </div>
      <div className={`h-12 rounded ${dim ? "bg-white/[0.05]" : "bg-white/[0.08]"}`} />
      <div className="grid grid-cols-2 gap-1.5">
        <div className={`h-2 rounded ${dim ? "bg-white/[0.05]" : "bg-white/[0.08]"}`} />
        <div className={`h-2 rounded ${dim ? "bg-white/[0.05]" : "bg-white/[0.08]"}`} />
      </div>
      <div
        className={`h-5 rounded mt-0.5 ${
          dim ? "bg-white/[0.06]" : "bg-gradient-to-r from-[#F4D64E] to-[#FFE583]"
        }`}
      />
      <div className="mt-1 flex items-baseline justify-between">
        <span className="text-[0.55rem] font-bold tracking-[0.2em] uppercase text-neutral-500">
          {label}
        </span>
        <span
          className={`font-display font-black text-[0.95rem] tabular-nums ${
            dim ? "text-neutral-400" : "text-white"
          }`}
        >
          {conv}
        </span>
      </div>
    </motion.div>
  );

  return (
    <VisualFrame tag="A/B test · Variant B winning">
      <div className="w-full max-w-md relative">
        <div className="grid grid-cols-2 gap-4">
          <Screen label="Variant A" dim conv="3.1%" delay={0.15} />
          <Screen label="Variant B" conv="4.6%" delay={0.3} />
        </div>
        {/* WINNER stamp lands on B */}
        <motion.span
          initial={{ opacity: 0, scale: 1.6, rotate: -8 }}
          animate={{ opacity: 1, scale: 1, rotate: -8 }}
          transition={{ duration: 0.5, delay: 0.95, ease: [0.5, 1.5, 0.5, 1] }}
          className="absolute top-2 right-2 px-3 py-1.5 border-2 border-[#F4D64E] rounded text-[0.62rem] font-bold tracking-[0.2em] uppercase text-[#F4D64E] bg-[#0B0F14]/80 backdrop-blur-sm pointer-events-none"
        >
          Winner · +48%
        </motion.span>
        {/* Hypothesis caption */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 1.1 }}
          className="mt-5 flex items-center gap-3 text-[0.6rem] font-bold tracking-[0.2em] uppercase"
        >
          <span className="text-[#F4D64E]">Hypothesis</span>
          <span className="h-px flex-1 bg-white/[0.08]" />
          <span className="text-neutral-400 normal-case tracking-normal text-[0.78rem] font-normal">
            Sticky ATC + bundle proof
          </span>
        </motion.div>
      </div>
    </VisualFrame>
  );
};

// 05 · Technical build direction → speed gauge with core web vitals
const SpeedVisual = () => (
  <VisualFrame tag="Core Web Vitals · Pass">
    <div className="w-full max-w-md flex flex-col items-center gap-6">
      {/* Semicircular gauge */}
      <div className="relative w-56 h-28 overflow-hidden">
        <svg viewBox="0 0 200 100" className="w-full h-full">
          <path
            d="M 12 100 A 88 88 0 0 1 188 100"
            stroke="rgba(255,255,255,0.06)"
            strokeWidth="14"
            fill="none"
            strokeLinecap="round"
          />
          <motion.path
            d="M 12 100 A 88 88 0 0 1 188 100"
            stroke="url(#sd-grad)"
            strokeWidth="14"
            fill="none"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 0.94 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          />
          <defs>
            <linearGradient id="sd-grad" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#F4D64E" />
              <stop offset="100%" stopColor="#FFE583" />
            </linearGradient>
          </defs>
        </svg>
        <div className="absolute inset-x-0 bottom-1 flex flex-col items-center">
          <span className="font-display font-black text-white text-[1.6rem] tabular-nums leading-none">94</span>
          <span className="text-[0.55rem] font-bold tracking-[0.22em] uppercase text-neutral-500 mt-1">Performance</span>
        </div>
      </div>
      {/* Pills */}
      <div className="flex flex-wrap items-center justify-center gap-2">
        {[["LCP", "1.6s"], ["CLS", "0.02"], ["INP", "120ms"]].map(([k, v]) => (
          <span key={k} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-[#F4D64E]/30 bg-[#F4D64E]/[0.05]">
            <span className="text-[0.6rem] font-bold tracking-[0.18em] uppercase text-[#F4D64E]">{k}</span>
            <span className="text-[0.75rem] font-bold tabular-nums text-white">{v}</span>
          </span>
        ))}
      </div>
    </div>
  </VisualFrame>
);

// 06 · Post-launch optimization → quarterly trend with area fill + final-point callout
const TrendVisual = () => {
  // 5 points (Q1..Q5) with rising conversion rate, normalized to a
  // 320x140 svg viewBox. Y-axis label values map to those Y coords.
  const points = [
    [30, 110], [95, 92], [160, 70], [225, 50], [300, 28],
  ];
  const yLabels = [
    { y: 14,  text: "5%" },
    { y: 56,  text: "4%" },
    { y: 98,  text: "3%" },
    { y: 138, text: "2%" },
  ];
  const lineD = points.map(([x, y], i) => `${i === 0 ? "M" : "L"} ${x} ${y}`).join(" ");
  // Closed path for the area fill (line + back along the baseline)
  const areaD = `${lineD} L ${points[points.length - 1][0]} 140 L ${points[0][0]} 140 Z`;
  return (
    <VisualFrame tag="Quarterly · Compounding">
      <div className="w-full max-w-md">
        <svg viewBox="0 0 320 140" className="w-full">
          {/* Y-axis grid + labels */}
          {yLabels.map((g, i) => (
            <g key={i}>
              <line
                x1="22" x2="320"
                y1={g.y} y2={g.y}
                stroke="rgba(255,255,255,0.05)"
                strokeDasharray="3 4"
              />
              <text
                x="0" y={g.y + 4}
                className="fill-neutral-500"
                style={{ font: "bold 8px sans-serif", letterSpacing: "0.18em", textTransform: "uppercase" }}
              >
                {g.text}
              </text>
            </g>
          ))}
          {/* Area fill under the trend */}
          <motion.path
            d={areaD}
            fill="url(#sd-area)"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 1.0 }}
          />
          {/* Trend line */}
          <motion.path
            d={lineD}
            stroke="url(#sd-trend)"
            strokeWidth="3"
            fill="none"
            strokeLinecap="round"
            initial={{ pathLength: 0 }}
            animate={{ pathLength: 1 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
          />
          {/* Dots */}
          {points.map(([x, y], i) => {
            const isFinal = i === points.length - 1;
            return (
              <g key={i}>
                {isFinal && (
                  <motion.circle
                    cx={x} cy={y} r={11}
                    fill="rgba(244,214,78,0.18)"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.5, delay: 1.4 }}
                  />
                )}
                <motion.circle
                  cx={x} cy={y}
                  r={isFinal ? 5.5 : 4}
                  fill="#F4D64E"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.4, delay: 0.4 + i * 0.18 }}
                />
              </g>
            );
          })}
          {/* Final-point callout label */}
          <motion.g
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 1.5, ease: [0.22, 1, 0.36, 1] }}
          >
            <rect x="218" y="2" width="92" height="18" rx="4" fill="#F4D64E" />
            <text
              x="264" y="14"
              textAnchor="middle"
              fill="#0B0F14"
              style={{ font: "bold 9px sans-serif", letterSpacing: "0.18em", textTransform: "uppercase" }}
            >
              Q5 · +62%
            </text>
          </motion.g>
          <defs>
            <linearGradient id="sd-trend" x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor="#F4D64E" />
              <stop offset="100%" stopColor="#FFE583" />
            </linearGradient>
            <linearGradient id="sd-area" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="rgba(244,214,78,0.28)" />
              <stop offset="100%" stopColor="rgba(244,214,78,0)" />
            </linearGradient>
          </defs>
        </svg>
        <div className="mt-3 flex items-center justify-between text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500 pl-6">
          <span>Q1</span><span>Q2</span><span>Q3</span><span>Q4</span><span>Q5</span>
        </div>
      </div>
    </VisualFrame>
  );
};

const DELIVERABLE_VISUALS = [
  FunnelVisual,
  AOVVisual,
  PDPVisual,
  ABTestVisual,
  SpeedVisual,
  TrendVisual,
];

const Deliverables = () => {
  const cms = useContent("conversion");
  const d = (cms && cms.deliverables) || {};
  const eyebrow = d.eyebrow || "Core deliverables";
  const headlineMain = d.headline_main || "What you get,";
  const headlineItalic = d.headline_italic || "in writing.";
  const items = Array.isArray(d.items) && d.items.length
    ? d.items.map((it, i) => ({
        n: String(i + 1).padStart(2, "0"),
        title: it.title || "",
        body: it.body || "",
      }))
    : DELIVERABLES_DEFAULTS;

  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const [inView, setInView] = useState(false);
  const sectionRef = useRef(null);

  // Only start the rotation once the section enters the viewport.
  // Before that, `active` stays on 0 and the visual sits idle so
  // visitors who haven't scrolled here yet aren't burning a hidden
  // animation loop.
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Auto-rotate every 4s. Only ticks while the section is in view
  // AND the user isn't hovering the tabs. Re-syncs whenever items
  // count changes.
  useEffect(() => {
    if (!inView || paused) return;
    const id = setInterval(() => {
      setActive((a) => (a + 1) % items.length);
    }, 4000);
    return () => clearInterval(id);
  }, [inView, paused, items.length]);

  const ActiveVisual = DELIVERABLE_VISUALS[active % DELIVERABLE_VISUALS.length];

  return (
    <section ref={sectionRef} data-testid="conversion-deliverables" className="relative bg-[#0B0F14] border-t border-white/[0.06]">
      {/* ============ MOBILE — accordion: visual lives UNDER each
          row's description when that row is active ============ */}
      <div className="lg:hidden">
        <div className="max-w-7xl mx-auto px-6 py-24 md:py-28">
          <div className="flex flex-col gap-5 max-w-3xl mb-10">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>
            <h2
              data-reveal="mask-block"
              className="font-display text-[2.25rem] md:text-[3rem] font-black tracking-[-0.035em] leading-[1.02] text-white"
            >
              <span>
                {headlineMain}{" "}
                <span className="italic font-normal text-neutral-400">{headlineItalic}</span>
              </span>
            </h2>
          </div>

          {/* Clickable list — each row expands inline with body + visual */}
          <ul className="flex flex-col">
            {items.map((d2, i) => {
              const isActive = i === active;
              const RowVisual = DELIVERABLE_VISUALS[i % DELIVERABLE_VISUALS.length];
              return (
                <li
                  key={d2.n + d2.title}
                  className={`border-t border-white/[0.08] first:border-t-0 ${
                    isActive ? "" : "hover:bg-white/[0.012]"
                  } transition-colors duration-300`}
                >
                  {/* Row header — tap target only (no body inside the button) */}
                  <button
                    type="button"
                    onClick={() => {
                      setActive(i);
                      setPaused(true);
                    }}
                    data-testid={`deliverable-row-m-${d2.n}`}
                    className="w-full text-left grid grid-cols-[2.75rem_1fr] gap-x-4 items-start py-5"
                  >
                    <span
                      className={`mt-1 text-[0.7rem] font-bold tracking-[0.22em] uppercase tabular-nums transition-colors duration-300 ${
                        isActive ? "text-[#F4D64E]" : "text-neutral-500"
                      }`}
                    >
                      {d2.n}
                    </span>
                    <h3
                      className={`font-display text-[1.15rem] sm:text-[1.25rem] font-black tracking-[-0.02em] leading-[1.18] transition-colors duration-300 ${
                        isActive ? "text-white" : "text-neutral-500"
                      }`}
                    >
                      {d2.title}
                    </h3>
                  </button>

                  {/* Expanded panel — body + visual at FULL row width */}
                  <motion.div
                    initial={false}
                    animate={{
                      height: isActive ? "auto" : 0,
                      opacity: isActive ? 1 : 0,
                    }}
                    transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <div className="pb-7 pl-[3.7rem] pr-0">
                      <p className="text-[0.92rem] text-neutral-400 leading-[1.65]">
                        {d2.body}
                      </p>
                      {/* Per-row visual — full row width, animates on activation */}
                      <div className="mt-6 -ml-[3.7rem]">
                        {isActive ? (
                          <motion.div
                            key={`m-vis-${d2.n}`}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                          >
                            <RowVisual />
                          </motion.div>
                        ) : null}
                      </div>
                    </div>
                  </motion.div>
                </li>
              );
            })}
            {/* Progress bar — mirrors desktop's auto-rotate indicator */}
            <li className="mt-7 flex items-center gap-3 text-[0.6rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
              <span className="tabular-nums text-[#F4D64E]">
                {items[active].n}
              </span>
              <div className="flex-1 h-px bg-white/[0.06] relative overflow-hidden">
                <motion.span
                  key={`m-${active}-${inView}-${paused}`}
                  className="absolute inset-y-0 left-0 bg-[#F4D64E]"
                  initial={{ width: 0 }}
                  animate={{ width: inView && !paused ? "100%" : "0%" }}
                  transition={{ duration: inView && !paused ? 4 : 0.25, ease: "linear" }}
                />
              </div>
              <span className="tabular-nums">
                of {String(items.length).padStart(2, "0")}
              </span>
            </li>
          </ul>
        </div>
      </div>

      {/* ============ DESKTOP — sticky left list, swapping right visual ============ */}
      <div className="hidden lg:block">
        <div className="max-w-7xl mx-auto px-10 py-24 xl:py-28">
          {/* Section header */}
          <div className="grid grid-cols-12 gap-x-12 mb-16">
            <div className="col-span-5">
              <span data-reveal="eyebrow">
                <Eyebrow>{eyebrow}</Eyebrow>
              </span>
              <h2
                data-reveal="mask-block"
                style={{ "--i": 1 }}
                className="mt-6 font-display text-[2.5rem] xl:text-[2.85rem] font-extrabold tracking-[-0.035em] leading-[1.02] text-white"
              >
                <span>
                  {headlineMain}{" "}
                  <span className="italic font-normal text-neutral-400">{headlineItalic}</span>
                </span>
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-12 gap-x-12">
            {/* LEFT — clickable list. Hover-pauses the rotation only
                while the cursor is over the tabs themselves, so users
                can read without losing position. */}
            <ul
              className="col-span-6 flex flex-col"
              onMouseEnter={() => setPaused(true)}
              onMouseLeave={() => setPaused(false)}
            >
              {items.map((d2, i) => {
                const isActive = i === active;
                return (
                  <li key={d2.n + d2.title}>
                    <button
                      type="button"
                      onClick={() => setActive(i)}
                      data-testid={`deliverable-row-${d2.n}`}
                      className={`w-full text-left grid grid-cols-[3rem_1fr] gap-x-5 items-start py-6 border-t border-white/[0.08] first:border-t-0 transition-colors duration-300 ${
                        isActive ? "" : "hover:bg-white/[0.012]"
                      }`}
                    >
                      <span
                        className={`mt-1 text-[0.72rem] font-bold tracking-[0.22em] uppercase tabular-nums transition-colors duration-300 ${
                          isActive ? "text-[#F4D64E]" : "text-neutral-500"
                        }`}
                      >
                        {d2.n}
                      </span>
                      <div>
                        <h3
                          className={`font-display text-[1.35rem] xl:text-[1.5rem] font-black tracking-[-0.02em] leading-[1.12] transition-colors duration-300 ${
                            isActive ? "text-white" : "text-neutral-500"
                          }`}
                        >
                          {d2.title}
                        </h3>
                        <motion.p
                          initial={false}
                          animate={{
                            height: isActive ? "auto" : 0,
                            opacity: isActive ? 1 : 0,
                            marginTop: isActive ? 12 : 0,
                          }}
                          transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                          className="overflow-hidden text-[0.95rem] text-neutral-400 leading-[1.65]"
                        >
                          {d2.body}
                        </motion.p>
                      </div>
                    </button>
                  </li>
                );
              })}
              {/* Progress bar — visualizes the auto-rotate timing */}
              <li className="mt-8 flex items-center gap-3 text-[0.62rem] font-bold tracking-[0.22em] uppercase text-neutral-500">
                <span className="tabular-nums text-[#F4D64E]">
                  {items[active].n}
                </span>
                <div className="flex-1 h-px bg-white/[0.06] relative overflow-hidden">
                  <motion.span
                    key={`${active}-${inView}-${paused}`}
                    className="absolute inset-y-0 left-0 bg-[#F4D64E]"
                    initial={{ width: 0 }}
                    animate={{ width: inView && !paused ? "100%" : "0%" }}
                    transition={{ duration: inView && !paused ? 4 : 0.25, ease: "linear" }}
                  />
                </div>
                <span className="tabular-nums">
                  of {String(items.length).padStart(2, "0")}
                </span>
              </li>
            </ul>

            {/* RIGHT — sticky shared visual that swaps via crossfade */}
            <div className="col-span-6 col-start-7">
              <div className="sticky top-32">
                <motion.div
                  key={active}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
                >
                  <ActiveVisual />
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   05 · Integration — "How this connects" (keeps positioning intact)
============================================================ */
const Connects = () => {
  const cms = useContent("conversion");
  const c = (cms && cms.connects) || {};
  const eyebrow = c.eyebrow || "How this connects";
  const headline = c.headline ||
    "CRO isn't a separate service. It's the lens we already use.";
  const body = c.body ||
    "If you've engaged us for a Shopify build, a brand refresh, or growth marketing, you're already working inside this process — we just don't always put a CRO label on it. This page exists so the discipline behind the work is visible.";
  return (
    <section data-testid="conversion-connects" className="relative bg-[#11161D] border-t border-white/[0.06]">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-x-12 gap-y-10">
          <div className="lg:col-span-4">
            <span data-reveal="eyebrow">
              <Eyebrow>{eyebrow}</Eyebrow>
            </span>
          </div>
          <div className="lg:col-span-8">
            <h2
              data-reveal="mask-block"
              className="font-display text-[1.6rem] md:text-[2.25rem] font-bold tracking-[-0.025em] leading-[1.2] text-white max-w-[34ch]"
            >
              <span>{headline}</span>
            </h2>
            <p
              data-reveal="mask-block"
              style={{ "--i": 1 }}
              className="mt-6 max-w-[60ch] text-[0.98rem] md:text-[1.05rem] text-neutral-400 leading-[1.7]"
            >
              <span>{body}</span>
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ============================================================
   Page composition
============================================================ */
export default function ConversionPage() {
  const { open: openContact } = useContactPanel();

  useSeo({
    route: "/conversion-optimization",
    fallback: {
      title: "Conversion Optimization — Sunday Digital",
      description:
        "Lift, earned with process — not guesses. CRO woven through every Shopify build and growth engagement: analyze, structure, build, test, iterate.",
    },
  });

  useEffect(() => {
    document.title = "Conversion Optimization — Sunday Digital";
    window.scrollTo({ top: 0, behavior: "instant" });
  }, []);

  // Reveal observer — same setup as HomeV5 / Marketing
  useEffect(() => {
    const root = document.querySelector('[data-variant="v5"]');
    if (!root) return;
    const els = root.querySelectorAll("[data-reveal]");
    if (!els.length) return;
    if (typeof IntersectionObserver === "undefined") {
      els.forEach((el) => el.classList.add("is-revealed"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.18, rootMargin: "0px 0px -8% 0px" }
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <div
      data-testid="conversion-page"
      data-variant="v5"
      className="min-h-screen bg-[#0B0F14] text-neutral-100 font-body"
    >
      <Nav />
      <main>
        <ConversionHero onPrimary={openContact} />
        <WhyCRO />
        <Approach />
        <Deliverables />
        <Connects />
        <TestimonialsV5 contentKey="conversion" />
        <ClosingV5 />
      </main>
      <Footer />
    </div>
  );
}
